import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiHttpService } from 'src/app/core/services/apiHttpcall.service';
import { DataResolverService } from 'src/app/core/services/data-resolver.service';
import { ProfileService } from 'src/app/core/services/profile.service';

@Component({
  selector: 'auth-page',
  templateUrl: './auth-page.component.html',
  styleUrls: ['./auth-page.component.scss'],
})
export class AuthPageComponent implements OnInit {
  loginPage: boolean;
  forgetPasswordPage: boolean;
  otpPage: boolean = false;
  reset_pwd: boolean = false;
  email: string;
  resp: any = undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpService: ApiHttpService,
    private profileService: ProfileService,
    private dataResolver: DataResolverService
  ) {
    this.loginPage = false;
    this.forgetPasswordPage = false;
    this.otpPage = false;
  }

  async handleRemberMe() {
    // Wait for profile data to be loaded first
    await this.dataResolver.resolve(null);

    // Get first accessible route based on user permissions
    const firstAccessibleRoute = this.getFirstAccessibleRoute();
    this.router.navigate([firstAccessibleRoute]);
  }

  private getFirstAccessibleRoute(): string {
    const userPermissions = this.getUserPermissionsFromStorage();
    if (!userPermissions) {
      return '/dashboard'; // Fallback if no permissions
    }

    // Use ProfileService to get the best route dynamically
    const isCustomer = this.profileService.userProfileData?.is_customer === 1;

    if (isCustomer) {
      // For customers, check customer-specific routes first
      if (this.hasPermissionForRoute('Dashboard.Dashboard.view', userPermissions)) {
        return '/customer-dashboard/map';
      }
      if (this.hasPermissionForRoute('Questionnaire.Questionnaire.view', userPermissions)) {
        return '/settings/templates/list';
      }
      if (this.hasPermissionForRoute('Contracts.Contracts.view', userPermissions)) {
        return '/settings/templates/list';
      }
    }

    // Check all available routes dynamically based on permissions
    const availableRoutes = this.getAllAvailableRoutes(userPermissions);

    if (availableRoutes.length > 0) {
      return availableRoutes[0]; // Return first available route
    }

    // Fallback to dashboard if no specific route found
    return '/dashboard';
  }

  private getAllAvailableRoutes(userPermissions: any): string[] {
    const routes: string[] = [];

    // Check all permission categories dynamically
    Object.keys(userPermissions).forEach(category => {
      const categoryPermissions = userPermissions[category];

      if (categoryPermissions && typeof categoryPermissions === 'object') {
        Object.keys(categoryPermissions).forEach(item => {
          const itemPermissions = categoryPermissions[item];

          if (itemPermissions && itemPermissions.view === true) {
            // Map category.item to route
            const route = this.getRouteForPermission(category, item);
            if (route && !routes.includes(route)) {
              routes.push(route);
            }
          }
        });
      }
    });

    return routes;
  }

  private getRouteForPermission(category: string, item: string): string | null {
    // Map permission categories to routes
    const routeMap: { [key: string]: { [key: string]: string } } = {
      'Dashboard': {
        'Dashboard': '/customer-dashboard/map'
      },
      'Questionnaire': {
        'Questionnaire': '/settings/templates/list'
      },
      'Contracts': {
        'Contracts': '/settings/templates/list'
      },
      'ManageTrips': {
        'Calendar': '/manage-trips/calendar/list',
        'TripRequests': '/manage-trips/trip-requests/list',
        'TripRejects': '/manage-trips/trip-rejects/list'
      },
      'Settings': {
        'Users': '/settings/users/list',
        'Roles': '/settings/roles/list',
        'Trucks': '/settings/trucks/list',
        'Trailers': '/settings/trailers/list',
        'Drivers': '/settings/drivers/list',
        'Locations': '/settings/locations/list',
        'Exceptions': '/settings/exceptions/list',
        'Templates': '/settings/templates/list'
      },
      'Setup': {
        'Services': '/setup/services/list',
        'Legs': '/setup/legs/list',
        'Milestones': '/setup/milestones/list',
        'Actions': '/setup/actions/list'
      },
      'Customers': {
        'Leads': '/customers/leads/list',
        'ActiveCustomers': '/settings/customers',
        'InactiveCustomers': '/customers/inactive-customers/list',
        'RejectedLeads': '/customers/rejectedlead/list'
      },
      'Reports': {
        'Report': '/reports/detail'
      },
      'DispatcherDashboard': {
        'DispatcherDashboard': '/dashboards/dispatcher-dashboard'
      },
      'LogisticsDashboard': {
        'LogisticsDashboard': '/dashboards/lc-dashboard'
      }
    };

    return routeMap[category]?.[item] || null;
  }

  private getUserPermissionsFromStorage(): any {
    try {
      const userPermissions = localStorage.getItem('user_permissions');
      if (userPermissions) {
        return JSON.parse(userPermissions);
      }
      return null;
    } catch (error) {
      console.error('Error parsing user permissions from localStorage:', error);
      return null;
    }
  }

  private hasPermissionForRoute(requiredPermission: string, userPermissions: any): boolean {
    if (!requiredPermission || !userPermissions) {
      return false;
    }

    // Parse permission string format: "category.item.action"
    const [category, item, action] = requiredPermission.split('.');

    if (!category || !item || !action) {
      return false;
    }

    // Check if user has the specific permission
    const categoryPermissions = userPermissions[category];
    if (!categoryPermissions) {
      return false;
    }

    const itemPermissions = categoryPermissions[item];
    if (!itemPermissions) {
      return false;
    }

    // Check the specific action (view, edit, create, etc.)
    return itemPermissions[action] === true;
  }

  handleSuccess(email: string) {
    if (this.forgetPasswordPage) {
      this.forgetPasswordPage = false;
      this.otpPage = true;
      this.email = email;
    } else if (this.otpPage) {
      this.otpPage = false;
      this.reset_pwd = true;
      this.email = email;
    }
  }

  navigateToLogin(){
    this.router.navigate(['/login']);
  }


  ngOnInit() {
    this.route.url.subscribe((url) => {
      this.loginPage = this.router.url.includes('login');
      this.forgetPasswordPage = this.router.url.includes('forgot-password');

      if (this.loginPage) {
        this.handleRemberMe();
      }
    });
}
}
